import React, { useState, useRef } from "react";
import { Form, Alert, Button } from "react-bootstrap";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getImageFullUrl } from '../../services/ApiService'; //@@@

/**
 * SimpleImageUpload Component
 *
 * A simple image upload component with file input button and preview.
 * Renvoye l'objet File directement pour permettre le recadrage dans le composant parent.
 *
 * @param {Object} props
 * @param {string} props.value - L'URL d'aperçu (chemin BDD ou Blob URL)
 * @param {Function} props.onChange - Callback fonction qui reçoit l'objet File binaire ou null
 * @param {number} props.maxSizeMB - Maximum file size in MB (default: 5)
 * @param {string[]} props.acceptedFormats - Accepted image formats 
 * @param {string} props.buttonLabel - Label for the upload button
 * @param {boolean} props.disabled - Whether the upload is disabled
 */
const SimpleImageUpload = ({
  value = null, // Vient maintenant de getPreviewUrl (chemin BDD ou URL Blob)
  onChange,
  maxSizeMB = 5,
  acceptedFormats = ["image/png", "image/jpeg", "image/jpg"],
  buttonLabel = "Sélectionner un fichier",
  disabled = false,
}) => {
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  // Note: base64Preview n'est plus nécessaire ici car le parent gère la preview du File

  /**
   * Validates file type and size ONLY (pas de dimensions)
   * @param {File} file - The uploaded file
   */
  const handleFile = (file) => {
    setError("");

    // 1. Validation du type de fichier
    if (!acceptedFormats.includes(file.type)) {
      setError("Format non accepté. Seuls PNG et JPG (JPEG) sont autorisés.");
      return;
    }

    // 2. Validation de la taille du fichier
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      setError(
        `Le fichier est trop volumineux. Taille maximale : ${maxSizeMB}MB.`
      );
      return;
    }

    // Pas de validation de dimensions ni de ratio ! 
    // Le fichier binaire est renvoyé directement au parent (PreconisationsModal)
    onChange(file);
    
    // Pas besoin de lire le fichier, pas d'imageInfo ici, 
    // car l'information sera obtenue après le recadrage.
  };

  /**
   * Handles file input change
   */
  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  /**
   * Clears the uploaded image
   */
  const handleClear = () => {
    setError("");
    // Note: imageInfo n'est plus réinitialisé car on ne le calcule plus ici
    onChange(null); // Renvoie null au parent pour suppression
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Opens file picker
   */
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // ---------------------------------------------
  // Affichage (La logique d'affichage est simplifiée)
  // ---------------------------------------------

  // Si 'value' est fourni, c'est l'URL d'aperçu (string) fournie par getPreviewUrl() dans le modal parent
  const isImagePresent = !!value; 
  
  // NOTE: La logique de 'imageInfo' doit être réintégrée dans le Modal parent ou dans le Cropper
  // car ce composant ne lit plus les dimensions. Nous allons masquer cette section pour l'instant.

  return (
    <div className="simple-image-upload">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleInputChange}
        accept={acceptedFormats.join(",")}
        style={{ display: "none" }}
        disabled={disabled}
      />

      <div className="d-flex flex-column gap-2 mb-2" style={{ alignItems: "flex-start" }}>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleClick}
          disabled={disabled}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "0.875rem",
          }}
        >
          <ImageOutlinedIcon fontSize="small" />
          {buttonLabel}
        </Button>

        {isImagePresent && (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleClear}
            disabled={disabled}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              fontSize: "0.875rem",
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
            Supprimer
          </Button>
        )}
      </div>

      {isImagePresent && (
        <div
          style={{
            border: "1px solid #dee2e6",
            borderRadius: "8px",
            padding: "0.5rem",
            display: "inline-block",
            marginBottom: "0.5rem",
          }}
        >
          <img
            src={value} /* Affiche l'URL BDD ou l'URL Blob */
            alt="Preview"
            style={{
              maxWidth: "200px",
              maxHeight: "150px",
              objectFit: "contain",
              display: "block",
            }}
          />
          {/* L'imageInfo est masqué car non calculé ici. */}
        </div>
      )}

      {error && (
        <Alert variant="danger" className="mt-2 mb-0" style={{ fontSize: "0.875rem" }}>
          {error}
        </Alert>
      )}
    </div>
  );
};

export default SimpleImageUpload;